import styled from "styled-components";
import { useQueryParams } from "hooks";
import { useMediaQuery } from "melodies-source/utils/hooks";
import { TextInput } from "melodies-source/TextInput";
import { useEffect, useLayoutEffect, useState } from "react";
import { Button } from "melodies-source/Button";
import {
  cancelOptOut,
  resendOptOutEmail,
  requestOptOut,
  verifyOptOut,
} from "./optOutActions";
import { Spinner } from "melodies-source/Spinner";
import { ReactComponent as CheckIcon } from "assets/svg/check-circle-green.svg";
import { ReactComponent as EmailIcon } from "assets/svg/email.svg";
import HeaderBG from "./icons/HeaderBG";
import { SetLiveIcon } from "./icons/SETLive";

type OptOutStatus =
  | "initial"
  | "submittingEmail"
  | "emailSubmitted"
  | "emailResending"
  | "emailResent"
  | "confirmDelete"
  | "submittingConfirmation"
  | "deleteConfirmed"
  | "cancelConfirmed";

const OptOut = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<OptOutStatus>("initial");
  const [error, setError] = useState("");

  useLayoutEffect(() => {
    const loader = document.getElementById("loader");
    if (loader) loader.style.display = "none";
  }, []);

  const isMobile = useMediaQuery("(max-width: 767px)");
  const { code } = useQueryParams(["code"]);

  useEffect(() => {
    if (code) {
      setStatus("confirmDelete");
    }
  }, [code]);

  const onSubmitEmail = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setError("");
    setStatus("submittingEmail");

    const { status, message } = await requestOptOut(email);
    if (status === "failure") {
      setError(message ?? "Unable to process request");
      setStatus("initial");
    } else {
      setStatus("emailSubmitted");
    }
  };

  const onResendEmail = async () => {
    setError("");
    setStatus("emailResending");

    const { status, message } = await resendOptOutEmail(email);
    if (status === "failure") {
      setError(message ?? "Unable to process request");
      setStatus("emailSubmitted");
    } else {
      setStatus("emailResent");
    }
  };

  const onCancelRequest = async () => {
    setError("");
    setStatus("submittingConfirmation");

    const { status, message } = await cancelOptOut(code);
    if (status === "failure") {
      setError(message ?? "Unable to process request");
      setStatus("confirmDelete");
    } else {
      setStatus("cancelConfirmed");
    }
  };

  const onVerifyDelete = async () => {
    setError("");
    setStatus("submittingConfirmation");

    const { status, message } = await verifyOptOut(code);
    if (status === "failure") {
      setError(message ?? "Unable to process request");
      setStatus("confirmDelete");
    } else {
      setStatus("deleteConfirmed");
    }
  };

  return (
    <PageContainer>
      {!isMobile && <DesktopNavBar />}
      <OptOutContent
        email={email}
        error={error}
        isMobile={isMobile}
        onCancelRequest={onCancelRequest}
        onChangeEmailRequest={() => {
          setError("");
          setEmail("");
          setStatus("initial");
        }}
        onResendEmail={onResendEmail}
        onSubmitEmail={onSubmitEmail}
        onVerifyDelete={onVerifyDelete}
        setEmail={setEmail}
        status={status}
      />
      <Footer />
    </PageContainer>
  );
};

export default OptOut;

const OptOutContent = ({
  email,
  error,
  isMobile,
  onCancelRequest,
  onChangeEmailRequest,
  onResendEmail,
  onSubmitEmail,
  onVerifyDelete,
  setEmail,
  status,
}: {
  email: string;
  error: string;
  isMobile: boolean;
  onCancelRequest: () => void;
  onChangeEmailRequest: () => void;
  onResendEmail: () => void;
  onSubmitEmail: () => void;
  onVerifyDelete: () => void;
  setEmail: (v: string) => void;
  status: OptOutStatus;
}) => {
  switch (status) {
    case "initial":
    case "submittingEmail":
      return (
        <>
          <HeaderContainer height={isMobile ? "164px" : "227px"}>
            <BGContainer>
              <HeaderBG isMobile={isMobile} />
            </BGContainer>
            {isMobile && (
              <SetLiveContainer>
                <SetLiveIcon />
              </SetLiveContainer>
            )}
            <HeaderTextContainer
              style={{ width: "100%", paddingTop: isMobile ? 0 : 40 }}
            >
              <HeaderText color="#FFF">Want to delete your data?</HeaderText>
            </HeaderTextContainer>
          </HeaderContainer>
          <BodyContainer isMobile={isMobile}>
            <BodyText>
              Your privacy is important to us. If you would like us to remove
              any of your personal information we may have, please let us know
              which email address you have used on SET. We'll send you an email
              to that address with a link that will verify your email and start
              the process of deleting any personal information we might have of
              yours.
            </BodyText>
            <br />
            <br />
            {status === "submittingEmail" ? (
              <Spinner />
            ) : (
              <TextInput
                value={email}
                onChange={(text) => setEmail(text)}
                placeholder="john@email.com"
                leftIcon={<StyledEmailIcon />}
                hasError={!!error}
                helperText={error}
              />
            )}
            <br />
            <Button
              onClick={onSubmitEmail}
              disabled={status === "submittingEmail" || !email}
              style={{ width: "100%" }}
            >
              Send Me An Email
            </Button>
          </BodyContainer>
        </>
      );

    case "emailSubmitted":
    case "emailResending":
    case "emailResent":
      return (
        <>
          <HeaderContainer height="auto" style={{ marginTop: isMobile ? 0 : 71 }}>
            {isMobile && (
              <SetLiveContainer style={{ marginBottom: 45 }}>
                <SetLiveIcon dark />
              </SetLiveContainer>
            )}
            <StyledCheckIcon />
            <HeaderText color="#000" style={{ marginTop: 8 }}>
              Email sent successfully
            </HeaderText>
            <br />
            <BodyText>An email has been sent to {email}</BodyText>
          </HeaderContainer>
          <BodyContainer isMobile={isMobile}>
            <StatusBox>
              {status === "emailResending" ? (
                <Spinner />
              ) : (
                <BodyText>
                  Didn't get the email?{" "}
                  <LinkSpan onClick={onResendEmail}>Click here</LinkSpan> to
                  resend it. Or{" "}
                  <LinkSpan onClick={onChangeEmailRequest}>click here</LinkSpan>{" "}
                  to enter a different email address.
                </BodyText>
              )}
            </StatusBox>
            <br />
            {!!error && <StatusText style={{ color: "red" }}>{error}</StatusText>}
            {status === "emailResent" && (
              <StatusText>We've resent the email</StatusText>
            )}
          </BodyContainer>
        </>
      );

    case "confirmDelete":
    case "submittingConfirmation":
      return (
        <>
          <HeaderContainer height="auto" style={{ marginTop: isMobile ? 0 : 71 }}>
            {isMobile && (
              <SetLiveContainer style={{ marginBottom: 34 }}>
                <SetLiveIcon dark />
              </SetLiveContainer>
            )}
            <HeaderText color="#000" style={{ marginTop: isMobile ? 0 : 8 }}>
              Are you sure you want to delete your data?
            </HeaderText>
          </HeaderContainer>
          <BodyContainer isMobile={isMobile}>
            <ConfirmDeleteBox>
              <BodyText>
                Are you sure you want to continue with the deletion process?
              </BodyText>
              <br />
              <br />
              <Button
                disabled={status === "submittingConfirmation"}
                onClick={onVerifyDelete}
              >
                Yes, I'm Sure
              </Button>
            </ConfirmDeleteBox>
            <br />
            <br />
            {status === "submittingConfirmation" ? (
              <Spinner />
            ) : (
              <StatusText>
                <LinkSpan onClick={onCancelRequest}>Click here</LinkSpan> to
                cancel your request
              </StatusText>
            )}
            {!!error && (
              <>
                <br />
                <StatusText style={{ color: "red" }}>{error}</StatusText>
              </>
            )}
          </BodyContainer>
        </>
      );

    case "deleteConfirmed":
    case "cancelConfirmed":
      return (
        <>
          <HeaderContainer height="auto" style={{ marginTop: isMobile ? 0 : 71 }}>
            {isMobile && (
              <SetLiveContainer style={{ marginBottom: 45 }}>
                <SetLiveIcon dark />
              </SetLiveContainer>
            )}
            <StyledCheckIcon />
            <HeaderText color="#000" style={{ marginTop: 8 }}>
              Success!
            </HeaderText>
          </HeaderContainer>
          <BodyContainer isMobile={isMobile}>
            <ConfirmDeleteBox>
              <BodyText>
                {status === "deleteConfirmed"
                  ? "Your request has been received. Your information will be deleted from SET within 72 hours."
                  : "Your request was successfully canceled."}
              </BodyText>
              <DividerLine />
              {status === "deleteConfirmed" ? (
                <StatusText>
                  If you previously agreed to share your data through SET with
                  an artist or a third party, you'll need to send a request
                  directly to them to delete your data.
                </StatusText>
              ) : (
                <StatusText>
                  <LinkSpan onClick={() => window.location.assign("/")}>
                    Click here
                  </LinkSpan>{" "}
                  to return home.
                </StatusText>
              )}
            </ConfirmDeleteBox>
          </BodyContainer>
        </>
      );
  }
};

const DesktopNavBar = () => (
  <NavBar>
    <div style={{ display: "flex", alignItems: "center" }}>
      <div style={{ cursor: "pointer" }} onClick={() => window.location.assign("/")}>
        <SetLiveIcon dark />
      </div>
    </div>
  </NavBar>
);

const FOOTER_LINKS = [
  { label: "About SET", url: "https://about.set.live" },
  { label: "Privacy Policy", url: "https://set.live/privacy" },
  { label: "Terms of Use", url: "https://set.live/terms" },
  { label: "Contact Us", url: "https://www.max.live/contact" },
];

const Footer = () => (
  <FooterContainer>
    <FooterLinks>
      {FOOTER_LINKS.map((link) => (
        <FooterLink key={link.label} onClick={() => window.open(link.url)}>
          {link.label}
        </FooterLink>
      ))}
    </FooterLinks>
    <FooterCopyright>© {new Date().getFullYear()} Set.Live</FooterCopyright>
  </FooterContainer>
);

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f7fafc;
  height: 100vh;

  & > * {
    font-family: Poppins, sans-serif;
  }
`;

const BGContainer = styled.div`
  position: absolute;
  z-index: -1;
`;

const BodyContainer = styled.div<{ isMobile: boolean }>`
  text-align: center;
  padding: 0 22px;
  max-width: 410px;
  margin-top: ${({ isMobile }) => (isMobile ? "21px" : "45px")};
`;

const BodyText = styled.span`
  font-size: 15px;
  line-height: 22px;
`;

const ConfirmDeleteBox = styled.div`
  background: #ffffff;
  border: 1px solid #999999;
  border-radius: 12px;
  padding: 30px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DividerLine = styled.div`
  border-top: 1px solid #999;
  margin: 25px 0;
`;

const HeaderContainer = styled.div<{ height: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: ${({ height }) => height};
  z-index: 1;
`;

const HeaderText = styled.h1<{ color: string }>`
  font-weight: 600;
  font-size: 30px;
  line-height: 37px;
  text-align: center;
  color: ${({ color }) => color};
  margin: 0;
`;

const HeaderTextContainer = styled.div`
  text-align: center;
  margin-top: auto;
  margin-bottom: auto;
  margin-left: 92px;
  margin-right: 92px;
`;

const LinkSpan = styled.span`
  color: #006acc;
  cursor: pointer;
`;

const NavBar = styled.div`
  width: 100%;
  height: 68px;
  padding: 0 21px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  border-bottom: thin solid #97979744;
`;

const StatusBox = styled.div`
  background: #f1f6f9;
  border: 1px solid #cccccc;
  border-radius: 8px;
  padding: 12px 14px;
`;

const StatusText = styled.div`
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
`;

const SetLiveContainer = styled.div`
  margin-top: 13px;
  display: flex;
  align-items: center;
`;

const FooterContainer = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  padding-bottom: 35px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const FooterLink = styled.span`
  font-size: 12px;
  line-height: 15px;
  color: #000;
  cursor: pointer;
  margin: 0 12px;

  &:hover {
    text-decoration: underline;
  }
`;

const FooterCopyright = styled.div`
  font-size: 12px;
  margin-top: 20px;
`;

const StyledCheckIcon = styled(CheckIcon)`
  width: 48px;
  height: 48px;
`;

const StyledEmailIcon = styled(EmailIcon)`
  width: 18px;
  height: 18px;
`;
